import { useRef, useState, useCallback, useEffect } from 'react'
import { Box, Grid, Button, IconButton } from '@mui/material'
import FormCreator from '@/shared/FormCreator'
import Table from '@/shared/Table'
import { Edit, Delete } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import EditAccountDialog from '@/components/EditAccountDialog'
import CreateAccountDialog from '@/components/CreateAccountDialog'
import { connect, useSelector } from 'react-redux'
import PermissionsController from '@/components/PermissionsController'
import usePermissions from '@/hooks/usePermissions'
import generateUsername from '../../components/Watermark/GenerateUsername'
import fieldToSortStr from '@/libs/utils/fieldToSortStr'
import get from 'lodash/get'
import Watermark from '../../components/Watermark/Watermark'
import ConfirmDialog from '@/shared/ConfirmDialog'
import Typography from '@mui/material/Typography'
import toUpper from 'lodash/toUpper'
import dayjs from 'dayjs'
import debounce from '@mui/material/utils/debounce'
import unionBy from 'lodash/unionBy'


const mapStateToProps = ({ account, loading }) => ({
    accountList: account.accountList,
    isLoading: loading['account/getUserAccounts'],
    deleteLoading: loading['account/deleteAccount'],
})

const fieldMap = {
    email: 'EMAIL',
    companyName: 'COMP_NAME',
    role: 'ROLE',
    phone: 'PHONE',
    status: 'STATUS'
}

const AccountManagement = ({ dispatch, accountList, isLoading, deleteLoading }) => {

    const form = useRef()
    const { t } = useTranslation()
    const { data, paging = {} } = accountList || {}
    const [sort, setSort] = useState([{ field: 'email', sort: 'asc' }])
    const [page, setPage] = useState(0)
    const [pageSize, setPageSize] = useState(10)
    const [deleteUserData, setDeleteUserData] = useState()
    const companyPageRef = useRef(0)
    const [companyResults, setCompanyResults] = useState({})
    const { data: companyResultsData = [] } = companyResults
    const [rolesResults, setRolesResults] = useState({})
    const { data: rolesResultsData = [] } = rolesResults
    const [statusResults, setStatusResults] = useState({})
    const { data: statusResultsData = [] } = statusResults
    const [open, setOpen] = useState(false);
    const [editUserData, setEditUserData] = useState()

    const selectedRole = form.current?.getValues()?.role || null

    const selectedStatus = form.current?.getValues()?.status || null

    const employeeId = useSelector(state => state.auth.account?.employeeId)

    const accountEmail = useSelector(state => state.auth.account?.accountEmail)

    const accountId = useSelector(state => state.auth.account?.accountId)

    const username = generateUsername(employeeId, accountEmail)

    const canDelete = usePermissions('account_delete')
    const canModify = usePermissions('account_modify')


    const btnProps = {
        size: 'midle',
        sx: { borderRadius: 2, padding: 0, color: theme => theme.palette.secondary_80.main },
    }

    const pagination = {
        ...paging,
        page,
        pageSize,
        onPageChange: setPage,
        onPageSizeChange: setPageSize,
    }

    const cols = [
        {
            field: 'email',
            headerName: t('general.account_name'),
            width: 240,
        },
        {
            field: 'phone',
            headerName: t('general.phone'),
            width: 120,
        },
        { field: 'companyName', headerName: t('general.company_name'), width: 200 },
        {
            field: 'role',
            headerName: t('general.account_type'),
            width: 180,
        },
        {
            field: 'period', headerName: t('general.authorization_duration'), width: 200,
            valueGetter: (params) => {
                const start = params?.row?.planStartTime
                const end = params?.row?.planEndTime
                const compId = params?.row?.companyId

                if (compId === 1) return t('general.permanent');

                if (!start || !end) return '-'

                return `${dayjs(start).format('YYYY/MM/DD')} ~ ${dayjs(end).format('YYYY/MM/DD')}`
            }
        },
        { field: 'status', headerName: t('general.status'), minWidth: 40, valueFormatter: ({ value }) => (value ? t(`status.${toUpper(value)}`) : '-'), },
        {
            field: 'edit',
            hidden: !canModify,
            headerName: t('general.edit'),
            minWidth: 10,
            renderCell: record =>
                record.row.editable === 1 && (
                    <IconButton
                        {...btnProps}
                        onClick={() => {
                            setEditUserData(record.row)
                        }}
                    >
                        <Edit />
                    </IconButton>
                ),
        },
        {
            field: 'delete',
            hidden: !canDelete,
            headerName: t('general.delete'),
            minWidth: 10,
            renderCell: record =>
                record.row.editable === 1 && (
                    <IconButton
                        {...btnProps}
                        onClick={() => {
                            setDeleteUserData(record.row)
                        }}
                    >
                        <Delete />
                    </IconButton>
                ),
        },
    ]

    const fetchData = useCallback(() => {
        const values = form.current?.getValues() || {}

        // 從 form 拿多欄位條件
        const payload = {
            pageIndex: page,
            pageSize,
            sort: fieldToSortStr(get(sort, '[0]'), fieldMap),
            email: values?.email?.trim() || null,
            company: values?.company?.label || null,
            role: values?.role?.value || null,
            name: values?.name?.trim() || null,
            phone: values?.phone?.trim() || null,
            status: values?.status?.value || null,
        }

        dispatch({
            type: 'account/getUserAccounts',
            payload,
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sort, page, pageSize, form])

    const getCompany = params => {
        const { companyPage = 0, keyword = '' } = params || {}

        companyPageRef.current = companyPage

        dispatch({
            type: 'account/getCompany',
            payload: {
                pageIndex: companyPageRef.current,
                pageSize: 10,
                keyword, //  關鍵字
            },
            successCallback: response => {
                const { data: responseData = [], paging: responsePaging = {} } = response || {}
                const { pageIndex } = responsePaging

                const newData = responseData.map(d => ({
                    value: d.companyId,
                    label: d.companyName,
                }))

                setCompanyResults(s => ({
                    data: pageIndex > 0 ? unionBy(s.data || [], newData, 'value') : newData,
                    paging: responsePaging,
                }))
            },
        })
    }

    const fetchCompanyData = useCallback(
        companyNameValue => {
            const keyword = companyNameValue || form.current?.getValues()?.companyName?.label
            const payload = {
                pageIndex: companyPageRef.current,
                pageSize: 10,
                keyword: keyword || '', // 空值也可以發送
            }

            dispatch({
                type: 'account/getCompany',
                payload,
                successCallback: response => {
                    const { data: responseData = [], paging: responsePaging = {} } = response || {}
                    const { pageIndex } = responsePaging

                    const newData = responseData.map(d => ({
                        value: d.companyId,
                        label: d.companyName,
                    }))

                    setCompanyResults(s => ({
                        data: pageIndex > 0
                            ? unionBy(s.data || [], newData, 'value')
                            : newData,
                        paging: responsePaging,
                    }))
                },
            })
        },
        [dispatch],
    )

    const setCompanyNameDebounce = debounce((_, value) => {
        companyPageRef.current = 0
        fetchCompanyData(value)
    }, 400)

    const getRoles = () => {
        const fixedRoles = [
            { value: 'Administrator', label: 'Admin' },
            { value: 'Owner', label: 'Owner' },
            { value: 'Operator', label: 'Operator' },
            { value: 'Viewer', label: 'Viewer' },
            { value: 'Customer_guest_user', label: 'Guest' },
        ]

        setRolesResults({
            data: fixedRoles
        })
    }

    const getStatus = () => {
        const fixedStatus = [
            { value: 'A', label: t('status.ACTIVE') },
            { value: 'IA', label: t('status.INACTIVE') },
        ]

        setStatusResults({
            data: fixedStatus
        })
    }

    const deleteUser = () => {
        if (deleteUserData) {
            dispatch({
                type: 'account/deleteAccount',
                payload: { userId: deleteUserData.userId },
                successCallback: () => {
                    dispatch({
                        type: 'app/notify',
                        payload: {
                            message: t('messages.account_deleted', { email: deleteUserData.email }),
                            severity: 'success',
                        },
                    })
                    setDeleteUserData()
                    fetchData()
                },
                errorCallback: () => {
                    dispatch({
                        type: 'app/notify',
                        payload: {
                            message: t('messages.account_delete_failed'),
                            severity: 'error',
                        },
                    });
                },
            })
        }
    }

    useEffect(() => {
        if (!accountId) return
        fetchData()
        getCompany()
        getRoles()
        getStatus()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accountId, fetchData, page, pageSize]);


    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: 520,
                    gap: 2,
                    border: '1px solid #2D2D6C',
                    borderRadius: 3,
                    overflow: 'hidden',
                    p: 2,
                    backgroundColor: '#fff',
                }}
            >
                {/* 搜尋區 */}
                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 2,
                        mb: 2,
                        alignItems: 'center',
                    }}
                >
                    <Grid item>
                        <FormCreator
                            ref={form}
                            formItems={[
                                {
                                    name: 'email',
                                    placeholder: t('general.insert_account_name'),
                                    defaultValue: '',
                                    inputConfig: {
                                        onSubmit: async () => {
                                            setPage(0)
                                            fetchData()
                                        },
                                    },
                                },
                                {
                                    type: 'autocomplete',
                                    name: 'company',
                                    defaultValue: null,
                                    inputConfig: {
                                        options: companyResultsData,
                                        isOptionEqualToValue: (option, value) => option?.value === value?.value,
                                        onChange: selectedOption => {
                                            form.current.setValue('company', selectedOption || null)
                                            form.current.setValue('companyName', selectedOption?.label || '')
                                            companyPageRef.current = 0
                                            fetchData()
                                        },
                                        onInputChange: (_, inputValue) => {
                                            setCompanyNameDebounce(_, inputValue) //  debounce 處理 keyword
                                        },
                                        placeholder: t('general.company_name'),
                                    },
                                },
                                {
                                    type: 'autocomplete',
                                    name: 'role',
                                    defaultValue: '',
                                    inputConfig: {
                                        options: rolesResultsData,
                                        value: selectedRole, //  這裡指定目前選中值
                                        isOptionEqualToValue: (option, value) => option?.value === value?.value,
                                        onChange: selectedOption => {
                                            form.current.setValue('role', selectedOption || null)
                                            form.current.setValue('roleName', selectedOption?.label || '')
                                            fetchData()
                                        },
                                        placeholder: t('general.role'),
                                    },
                                },
                                {
                                    type: 'autocomplete',
                                    name: 'status',
                                    inputConfig: {
                                        options: statusResultsData,
                                        value: selectedStatus, //  這裡指定目前選中值
                                        isOptionEqualToValue: (option, value) => option?.value === value?.value,
                                        onChange: selectedOption => {
                                            form.current.setValue('status', selectedOption || null)
                                            form.current.setValue('statusName', selectedOption?.label || '')
                                            fetchData()
                                        },
                                        placeholder: t('general.authorization_status'),
                                    },
                                },
                            ]}
                            flexDirection="row"
                            submitComponent={() => null}
                            resetComponent={() => null}
                            gridItemProps={{ mr: 3 }}
                        />
                    </Grid>
                    <Box sx={{ flexGrow: 1 }} />
                    <PermissionsController permission="account_create">
                        <Grid item mr={2}>
                            <Button
                                variant="contained"
                                style={{ backgroundColor: '#2D2D6C', color: '#fff' }}
                                onClick={() => setOpen(true)}
                            >
                                + 新增帳號
                            </Button>
                        </Grid>
                    </PermissionsController>
                </Box>

                {/* 表格區 */}
                <Table
                    minHeight={430}
                    columns={cols}
                    rows={data || []}
                    dataGridConfig={{
                        sortingMode: 'server',
                        sortingOrder: ['desc', 'asc'],
                        getRowId: row => row.userId,
                        sortModel: sort,
                        onSortModelChange: newSort => setSort(newSort),
                        loading: isLoading,
                    }}
                    noRowsLabel={t('errors.no_data')}
                    pagination={pagination}
                />
                <Watermark username={username} />
            </Box>

            <CreateAccountDialog
                open={open}
                onClose={() => setOpen(false)}
                onComplete={() => {
                    fetchData()
                }}
            />

            <EditAccountDialog
                open={!!editUserData}
                data={editUserData}
                onClose={() => {
                    setEditUserData()
                }}
                onComplete={() => {
                    fetchData()
                }}
            />

            <ConfirmDialog
                title={t('messages.confirm_delete')}
                open={!!deleteUserData}
                onClose={() => {
                    setDeleteUserData()
                }}
                onSubmit={() => {
                    deleteUser()
                }}
                loading={deleteLoading}
            >
                <Typography>
                    {t('messages.confirm_delete_email', { email: deleteUserData?.email })}
                </Typography>
            </ConfirmDialog>

        </>
    )
}

export default connect(mapStateToProps)(AccountManagement)